// Подписи и словари реестра РМРС (зеркало портала 55, таблицы p55_rs_*). Только показ.
//
// ЧТО ЗДЕСЬ ЛЕЖИТ И ПОЧЕМУ
// Реестр Регистра записан так, как его печатает сам Регистр: вид свидетельства — строкой
// «СТО-Свидетельство о типовом одобрении», область признания сервисника — абзацем на двести
// символов сразу по-русски и по-английски. В таблицу это не влезает и читать это невозможно,
// поэтому здесь короткие подписи. КОДЫ И ПОЛНЫЕ ТЕКСТЫ НЕ ВЫБРАСЫВАЕМ: короткая подпись идёт
// в ячейку, полная — в title и в карточку. Если Регистр заведёт новый вид свидетельства,
// он покажется своим кодом, а не пропадёт (см. видСвидетельства).
//
// ЧЕГО В ДАННЫХ НЕТ — тоже здесь, потому что об этом надо говорить на экране, а не молчать:
// названий групп номенклатуры РС (nomen_group пуст во всех 14 781 строках, на 28.07.2026), сроков
// освидетельствования судов, типа и класса судна. Экран печатает по этим пометкам «в данных
// нет», а не прочерк: прочерк у «ближайшего освидетельствования» читался бы как «всё в
// порядке». Фактическую заполненность отдаёт ядро (/p55/rs/refs → empty_columns), так что
// наполнится источник — и раздел это увидит сам, без правки этого файла.

import { daysPast, isoDay } from './format'

// ─────────── адреса разделов ───────────
// Ровно эти пути нужно добавить маршрутами в App.tsx.
// Карточка сервисника — НЕ сегментом пути: название компании в реестре содержит кавычки,
// точки и косые черты («ООО "Базовая Электрорадионавигационная камера"»), и в сегменте пути
// это либо ломается, либо превращается в нечитаемую простыню %-кодов. Поэтому компания
// передаётся параметром запроса, и маршрут у раздела один.
export const ПУТИ = {
  суда: '/rs/vessels',
  судно: (imo: string) => `/rs/vessels/${encodeURIComponent(imo)}`,
  изделия: '/rs/products',
  изделие: (id: string) => `/rs/products/${encodeURIComponent(id)}`,
  сервисники: '/rs/suppliers',
  сервисник: (company: string) => `/rs/suppliers?company=${encodeURIComponent(company)}`,
} as const

export const РАЗМЕР_СТРАНИЦЫ = 50   // как PAGE в p55_api.py

// ─────────── виды свидетельств ───────────
// В данных 18 видов, и записаны они как «КОД-Полное название». Разбираем по первому дефису:
// свой словарь кодов заводить нельзя — Регистр добавляет виды, и словарь молча устареет.

export interface ВидСвидетельства {
  code: string
  title: string
}

export function видСвидетельства(v?: string | null): ВидСвидетельства {
  const s = (v || '').trim()
  if (!s) return { code: '—', title: 'вид свидетельства в данных не указан' }
  const i = s.indexOf('-')
  // Дефиса нет — значит Регистр записал иначе; показываем как есть, а не «—».
  if (i <= 0) return { code: s, title: s }
  return { code: s.slice(0, i), title: s.slice(i + 1).trim() }
}

/**
 * Виды свидетельств, которые НЕ про изделие, а про предприятие, изготовителя или
 * лабораторию. Это 6 137 строк из 14 781 (на 28.07.2026): раздел называется «Одобренные
 * изделия», но заметная часть реестра — признание организации. Человек, который ищет
 * «одобрен ли прибор», обязан видеть разницу, иначе примет признание завода за одобрение типа.
 * Код CKK записан ЛАТИНСКОЙ C — так в данных Регистра, и исправлять источник мы не будем.
 */
export const ВИДЫ_ПРО_ПРЕДПРИЯТИЕ = new Set(['ССП', 'СП', 'СПИ', 'СПЛ', 'CKK', 'СПО', 'СПП(К)', '6.3.27'])

export const проПредприятие = (certificate_type?: string | null): boolean =>
  ВИДЫ_ПРО_ПРЕДПРИЯТИЕ.has(видСвидетельства(certificate_type).code)

// ─────────── области признания сервисников ───────────
// Их в данных ровно 11, и каждая — абзац по-русски и по-английски. Сопоставляем по
// характерному куску русского текста: сравнивать целиком нельзя (Регистр правит формулировки
// и добавляет ссылки на резолюции ИМО), а по куску подпись переживёт правку.
// Порядок важен: «береговое обслуживание радиооборудования ГМССБ» и «установка и ремонт
// радиооборудования» — разные области, и первая проверка должна быть более узкой.

const ОБЛАСТИ: Array<[RegExp, string]> = [
  [/АРБ\s+спутниковой/i, 'АРБ КОСПАС-САРСАТ (береговое ТО)'],
  [/регистраторов данных рейса/i, 'РДР / У-РДР (ежегодная проверка)'],
  [/радиооборудования ГМССБ/i, 'ГМССБ (береговое ТО и ремонт)'],
  [/радио- и навигационного оборудования/i, 'Радио- и навигационное оборудование'],
  [/спасательных шлюпок/i, 'Спасательные и дежурные шлюпки'],
  [/шлюпки дежурные/i, 'Дежурные и скоростные шлюпки (надувные)'],
  [/плоты спасательные/i, 'Спасательные плоты'],
  [/жилеты спасательные/i, 'Спасательные жилеты'],
  [/гидростатические разобщающие/i, 'Гидростатические разобщающие устройства'],
  [/эвакуационные системы/i, 'Морские эвакуационные системы'],
  [/уплотнения водонепроницаемых/i, 'Уплотнения кабельных проходов'],
]

/** Короткая подпись области признания. Не распознали — отдаём первые слова, а не «—». */
export function короткаяОбласть(scope?: string | null): string {
  const s = (scope || '').trim()
  if (!s) return 'область в данных не указана'
  for (const [шаблон, подпись] of ОБЛАСТИ) if (шаблон.test(s)) return подпись
  return s.length > 70 ? `${s.slice(0, 70)}…` : s
}

/** Наша собственная область работы — по ней раздел отбирает «кто нам конкурент по существу». */
export const НАША_ОБЛАСТЬ = 'Радио- и навигационное оборудование'

// ─────────── сроки ───────────
// Один расчёт на весь раздел, и он совпадает с фильтром ядра (p55_api.СКОРО_ДНЕЙ = 90):
// иначе плитка «истекает» и фильтр «истекает» показывали бы разное, и человек решил бы,
// что экран врёт. Число приходит из /p55/rs/refs (expiring_days), значение здесь — запас
// на случай, если справочники ещё не загрузились.
export const СКОРО_ДНЕЙ = 90

/**
 * Сколько дней осталось до даты. Ядро отдаёт days_left в строках изделий и признаний, но у
 * СГРУППИРОВАННЫХ строк (список сервисников — это компании) считать нечего: там только
 * максимальная дата. Считаем здесь, и обязательно через daysPast: разница в миллисекундах
 * сдвигала бы «истекает завтра» на «истекает сегодня» в зависимости от времени суток.
 */
export function осталосьДней(iso?: string | null): number | null {
  const день = isoDay(iso)
  if (!день) return null
  // 0 - x, а не -x: unary-минус от нуля даёт -0, и «истекает сегодня» в сравнениях
  // и в тестах ведёт себя как отдельное значение.
  return 0 - daysPast(день)
}

export type ВидСрока = 'нет' | 'истёк' | 'скоро' | 'годен'

export function видСрока(daysLeft: number | null | undefined, порог = СКОРО_ДНЕЙ): ВидСрока {
  if (daysLeft === null || daysLeft === undefined) return 'нет'
  if (daysLeft < 0) return 'истёк'
  if (daysLeft < порог) return 'скоро'
  return 'годен'
}

/** Класс бейджа из styles.css: won — зелёный, lose — красный, work — жёлтый, пусто — серый. */
export const классСрока = (в: ВидСрока): '' | 'won' | 'lose' | 'work' =>
  в === 'годен' ? 'won' : в === 'истёк' ? 'lose' : в === 'скоро' ? 'work' : ''

export function подписьСрока(daysLeft: number | null | undefined, порог = СКОРО_ДНЕЙ): string {
  const в = видСрока(daysLeft, порог)
  if (в === 'нет') return 'срока нет в данных'
  if (в === 'истёк') return `истёк ${дней(-(daysLeft as number))} назад`
  if (в === 'скоро') return `осталось ${дней(daysLeft as number)}`
  return 'действует'
}

/** «12 дней» / «1 день» — русские окончания, иначе получается «1 дней». */
export function дней(n: number): string {
  const a = Math.abs(n) % 100
  const b = a % 10
  if (a > 10 && a < 20) return `${Math.abs(n)} дней`
  if (b === 1) return `${Math.abs(n)} день`
  if (b >= 2 && b <= 4) return `${Math.abs(n)} дня`
  return `${Math.abs(n)} дней`
}

// ─────────── состояние свидетельства ───────────
// В данных status — свободный текст Регистра: «Действует», «Не действует», «Не действует
// Взамен выдан: …», «Приостановлено с 18/05/2026». Разбираем по началу строки и НЕ выбрасываем
// хвост: «взамен выдан» — это самое важное в строке «не действует».

export type СостояниеРС = 'действует' | 'не действует' | 'приостановлено' | 'иное'

export function состояние(status?: string | null): { вид: СостояниеРС; хвост: string } {
  const s = (status || '').trim()
  if (/^действует/i.test(s)) return { вид: 'действует', хвост: s.slice('действует'.length).trim() }
  if (/^не действует/i.test(s)) {
    return { вид: 'не действует', хвост: s.slice('не действует'.length).trim() }
  }
  if (/^приостановлено/i.test(s)) return { вид: 'приостановлено', хвост: s }
  return { вид: 'иное', хвост: s }
}

export const классСостояния = (в: СостояниеРС): '' | 'won' | 'lose' | 'work' =>
  в === 'действует' ? 'won' : в === 'не действует' ? 'lose' : в === 'приостановлено' ? 'work' : ''

// ─────────── номенклатура РС ───────────
// Кода мало, названия группы в данных нет (nomen_group пуст во всех строках). Своего словаря
// не заводим: 30 кодов можно было бы подписать по памяти, но подпись «06000000 — Судовые
// устройства» из головы окажется в отчёте как факт. Показываем код и говорим, чего нет.
export const НЕТ_НАЗВАНИЙ_НОМЕНКЛАТУРЫ =
  'Регистр отдаёт только код группы номенклатуры — названия групп в данных нет'

// ─────────── сборка строки запроса ───────────
// Пустые значения не отправляем: ядро игнорирует неизвестные параметры, но пустой `flag=`
// в ключе кэша плодил бы разные ключи на одинаковый запрос.

export type ФильтрыP55 = Record<string, string | number | boolean | undefined | null>

export function строкаЗапроса(ф: ФильтрыP55): string {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(ф)) {
    if (v === undefined || v === null || v === '' || v === false) continue
    p.set(k, v === true ? '1' : String(v))
  }
  const s = p.toString()
  return s ? `?${s}` : ''
}

// ─────────── свежесть снимка реестра ───────────
// Синк ходит в D1 раз в час, но сам реестр наполняет СКРАПЕР, и это разные сроки: synced_at
// говорит «зеркало свежее», scraped_at — «а вот обход rs-class.org был когда-то давно».
// Показывать только первое значило бы выдать позавчерашний снимок за сегодняшний реестр.

export function возрастДней(iso?: string | null): number | null {
  if (!iso) return null
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return null
  return Math.floor((Date.now() - t) / 86_400_000)
}
